export interface CedarBunkerSpec {
  id: string;
  /** Signed art-direction cue: sign picks the side; magnitude varies rough spacing. */
  x: number;
  /** Fraction of the current cup distance measured from the gate. */
  along: number;
  width: number;
  length: number;
  rotation: number;
  seed: number;
}

export interface ResolvedCedarBunker extends CedarBunkerSpec {
  centerZ: number;
  outer: Array<readonly [number, number]>;
  wallTop: Array<readonly [number, number]>;
  floor: Array<readonly [number, number]>;
}

export interface CedarFlatPath {
  startX: number;
  startZ: number;
  endX: number;
  endZ: number;
}

export type CedarGreenSize = 'compact' | 'medium' | 'large';

export interface CedarGreenLayout {
  size: CedarGreenSize;
  /** Green remaining behind the address ball, the tighter end of the ellipse. */
  addressMarginFt: number;
}

/**
 * Three of each size class, deliberately non-monotonic by putt length. The
 * radius helper adds these margins to the minimum distance needed to contain
 * the full putt, so “compact” never means clipping the address or cup.
 */
export const CEDAR_GREEN_LAYOUTS: Readonly<Record<number, CedarGreenLayout>> = {
  1: { size: 'large', addressMarginFt: 17 },
  2: { size: 'compact', addressMarginFt: 4 },
  3: { size: 'medium', addressMarginFt: 10 },
  4: { size: 'large', addressMarginFt: 17 },
  5: { size: 'compact', addressMarginFt: 4 },
  6: { size: 'large', addressMarginFt: 17 },
  7: { size: 'medium', addressMarginFt: 10 },
  8: { size: 'compact', addressMarginFt: 4 },
  9: { size: 'medium', addressMarginFt: 10 },
};

export const CEDAR_GROUND_SIZE_FT = 2000;

function steppedRange(start: number, end: number, step: number): number[] {
  const result: number[] = [];
  for (let value = start; value <= end; value += step) result.push(value);
  return result;
}

/**
 * A non-uniform 65 x 64 grid: six-foot cells around every supported green,
 * then progressively wider cells toward the 2,000-foot ground boundary.
 * This is 4,160 vertices / 8,064 triangles and remains Uint16-indexable.
 */
export const CEDAR_TERRAIN_X_COORDS: readonly number[] = Object.freeze([
  -1000,
  -650,
  -420,
  -300,
  -230,
  -190,
  ...steppedRange(-156, 156, 6),
  190,
  230,
  300,
  420,
  650,
  1000,
]);
export const CEDAR_TERRAIN_Z_COORDS: readonly number[] = Object.freeze([
  -1000,
  -700,
  -480,
  -340,
  -270,
  -230,
  ...steppedRange(-210, 90, 6),
  120,
  160,
  220,
  320,
  500,
  750,
  1000,
]);
export const CEDAR_TERRAIN_VERTEX_COUNT =
  CEDAR_TERRAIN_X_COORDS.length * CEDAR_TERRAIN_Z_COORDS.length;
export const CEDAR_TERRAIN_TRIANGLE_COUNT =
  (CEDAR_TERRAIN_X_COORDS.length - 1) *
  (CEDAR_TERRAIN_Z_COORDS.length - 1) *
  2;

// Fixed art direction makes every hole repeatable for screenshots and QA.
export const CEDAR_BUNKER_LAYOUTS: Readonly<Record<number, readonly CedarBunkerSpec[]>> = {
  1: [
    { id: 'left-guard', x: -8, along: 0.72, width: 4.8, length: 2.8, rotation: -0.31, seed: 101 },
  ],
  2: [
    { id: 'right-approach', x: 10.5, along: 0.48, width: 6.4, length: 3.1, rotation: 0.38, seed: 201 },
    { id: 'left-rear', x: -12, along: 0.86, width: 4.8, length: 2.5, rotation: -0.52, seed: 202 },
  ],
  3: [
    { id: 'left-approach', x: -11.5, along: 0.38, width: 7, length: 3.2, rotation: -0.21, seed: 301 },
    { id: 'right-guard', x: 10, along: 0.8, width: 5.5, length: 2.8, rotation: 0.49, seed: 302 },
  ],
  4: [
    { id: 'right-guard', x: 8.5, along: 0.74, width: 5.2, length: 2.6, rotation: 0.31, seed: 401 },
  ],
  5: [
    { id: 'right-fairway', x: 12.5, along: 0.31, width: 6.6, length: 3.2, rotation: 0.45, seed: 501 },
    { id: 'left-approach', x: -13, along: 0.6, width: 5.8, length: 3, rotation: -0.35, seed: 502 },
    { id: 'right-rear', x: 9.5, along: 0.88, width: 4.8, length: 2.5, rotation: 0.61, seed: 503 },
  ],
  6: [
    { id: 'left-approach', x: -10.5, along: 0.43, width: 6.5, length: 3, rotation: -0.38, seed: 601 },
    { id: 'right-guard', x: 11, along: 0.77, width: 5.2, length: 2.7, rotation: 0.28, seed: 602 },
  ],
  7: [
    { id: 'right-fairway', x: 14, along: 0.25, width: 7.8, length: 3.5, rotation: 0.35, seed: 701 },
    { id: 'left-cross', x: -13.5, along: 0.52, width: 6.5, length: 3.1, rotation: -0.49, seed: 702 },
    { id: 'right-guard', x: 11.5, along: 0.82, width: 5.8, length: 2.8, rotation: 0.56, seed: 703 },
  ],
  8: [
    { id: 'right-approach', x: 11, along: 0.55, width: 6.2, length: 3, rotation: 0.28, seed: 801 },
    { id: 'left-rear', x: -9.5, along: 0.86, width: 5, length: 2.5, rotation: -0.61, seed: 802 },
  ],
  9: [
    { id: 'left-approach', x: -8.5, along: 0.48, width: 5.4, length: 2.7, rotation: -0.35, seed: 901 },
    { id: 'right-guard', x: 8.8, along: 0.82, width: 4.6, length: 2.4, rotation: 0.49, seed: 902 },
  ],
};

export const CEDAR_BUNKER_POINT_COUNT = 18;

function outlinePoints(
  spec: CedarBunkerSpec,
  targetFt: number,
  radiusScale: number
): Array<readonly [number, number]> {
  const centerZ = -targetFt * spec.along;
  const cosR = Math.cos(spec.rotation);
  const sinR = Math.sin(spec.rotation);
  const points: Array<readonly [number, number]> = [];
  for (let i = 0; i < CEDAR_BUNKER_POINT_COUNT; i++) {
    const angle = (i / CEDAR_BUNKER_POINT_COUNT) * Math.PI * 2;
    const radius =
      radiusScale *
      (1 +
        0.16 * Math.sin(angle * 3 + spec.seed * 0.17) +
        0.08 * Math.sin(angle * 5 + spec.seed * 0.31));
    const localX = Math.cos(angle) * spec.width * 0.5 * radius;
    const localZ = Math.sin(angle) * spec.length * 0.5 * radius;
    points.push([
      spec.x + localX * cosR - localZ * sinR,
      centerZ + localX * sinR + localZ * cosR,
    ]);
  }
  return points;
}

export function resolveCedarBunkers(
  holeNumber: number,
  targetFt: number
): ResolvedCedarBunker[] {
  const hole = Math.max(1, Math.min(9, Math.round(holeNumber)));
  return (CEDAR_BUNKER_LAYOUTS[hole] ?? CEDAR_BUNKER_LAYOUTS[1]).map((source) => {
    const side = source.x < 0 ? -1 : 1;
    const centerZ = -targetFt * source.along;
    const greenCenterZ = -targetFt * 0.55;
    const roughRadius = cedarGreenRadiusForHole(hole, targetFt) + 3.8;
    const centerDeltaZ = centerZ - greenCenterZ;
    const edgeX =
      Math.abs(centerDeltaZ) < roughRadius
        ? Math.sqrt(roughRadius * roughRadius - centerDeltaZ * centerDeltaZ) / 1.25
        : 0;
    const variedGap = 1.4 + Math.max(0, Math.abs(source.x) - 8) * 0.13;
    const spec = {
      ...source,
      x: side * (edgeX + source.width * 0.5 + variedGap),
    };
    let outer = outlinePoints(spec, targetFt, 1);

    // Rotation and the irregular lobes can bring a corner closer than the
    // center-line estimate. Nudge the whole hazard outward until every point
    // is safely past the fringe and visibly rooted in rough.
    for (let attempt = 0; attempt < 4; attempt++) {
      const clearance = Math.min(
        ...outer.map(([x, z]) => cedarGreenZoneDistance(x, z, targetFt) - roughRadius)
      );
      if (clearance >= 1) break;
      spec.x += side * ((1 - clearance) / 1.1);
      outer = outlinePoints(spec, targetFt, 1);
    }
    return {
      ...spec,
      centerZ,
      outer,
      wallTop: outlinePoints(spec, targetFt, 0.78),
      floor: outlinePoints(spec, targetFt, 0.65),
    };
  });
}

export function cedarGreenRadiusForHole(holeNumber: number, targetFt: number): number {
  const hole = Math.max(1, Math.min(9, Math.round(holeNumber)));
  const layout = CEDAR_GREEN_LAYOUTS[hole] ?? CEDAR_GREEN_LAYOUTS[1];
  return Math.max(0, targetFt) * 0.55 + layout.addressMarginFt;
}

export function cedarGreenZoneDistance(x: number, z: number, targetFt: number): number {
  return Math.hypot(x * 1.25, z + targetFt * 0.55);
}

function smoothstep(edge0: number, edge1: number, value: number): number {
  const t = Math.max(0, Math.min(1, (value - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

function distanceToSegment(
  x: number,
  z: number,
  startX: number,
  startZ: number,
  endX: number,
  endZ: number
): number {
  const dx = endX - startX;
  const dz = endZ - startZ;
  const lengthSquared = dx * dx + dz * dz;
  const t =
    lengthSquared > 0
      ? Math.max(0, Math.min(1, ((x - startX) * dx + (z - startZ) * dz) / lengthSquared))
      : 0;
  return Math.hypot(x - (startX + dx * t), z - (startZ + dz * t));
}

function pointIsInsidePolygon(
  points: ReadonlyArray<readonly [number, number]>,
  x: number,
  z: number
): boolean {
  let inside = false;
  for (let current = 0, previous = points.length - 1; current < points.length; previous = current++) {
    const [currentX, currentZ] = points[current];
    const [previousX, previousZ] = points[previous];
    if (
      currentZ > z !== previousZ > z &&
      x < ((previousX - currentX) * (z - currentZ)) / (previousZ - currentZ) + currentX
    ) {
      inside = !inside;
    }
  }
  return inside;
}

function distanceOutsidePolygon(
  points: ReadonlyArray<readonly [number, number]>,
  x: number,
  z: number
): number {
  if (pointIsInsidePolygon(points, x, z)) return 0;
  let closest = Number.POSITIVE_INFINITY;
  for (let current = 0; current < points.length; current++) {
    const next = (current + 1) % points.length;
    closest = Math.min(
      closest,
      distanceToSegment(x, z, ...points[current], ...points[next])
    );
  }
  return closest;
}

/**
 * Broad Cedar-only rough contours. The complete green, fringe, bunker apron,
 * and address-to-cup line remain exactly flat. Six deterministic low landforms
 * vary by hole, then ease back to zero well before the sparse horizon cells.
 */
export function cedarTerrainHeightAt(
  holeNumber: number,
  targetFt: number,
  bunkers: readonly ResolvedCedarBunker[],
  x: number,
  z: number,
  additionalFlatPath?: CedarFlatPath
): number {
  const hole = Math.max(1, Math.min(9, Math.round(holeNumber)));
  const greenRadius = cedarGreenRadiusForHole(hole, targetFt);
  const distanceFromGreen = cedarGreenZoneDistance(x, z, targetFt);
  const centerZ = -targetFt * 0.55;
  const localEdge = Math.max(Math.abs(x), Math.abs(z - centerZ));
  const edgeFade = 1 - smoothstep(150, 205, localEdge);
  if (edgeFade <= 0) return 0;

  // The fringe ends at +3.8 ft. Preserve another eleven-foot rough apron so
  // every six-foot terrain cell touching visible fringe has four zero-height
  // corners, then blend gradually enough to avoid an artificial berm.
  let protection = smoothstep(
    greenRadius + 15,
    greenRadius + 39,
    distanceFromGreen
  );

  // This capsule is intentionally independent of the ellipse. It is mostly
  // redundant today, but guarantees a flat roll if the green art changes.
  protection = Math.min(
    protection,
    smoothstep(4, 18, distanceToSegment(x, z, 0, 0, 0, -targetFt))
  );
  if (additionalFlatPath) {
    protection = Math.min(
      protection,
      smoothstep(
        4,
        18,
        distanceToSegment(
          x,
          z,
          additionalFlatPath.startX,
          additionalFlatPath.startZ,
          additionalFlatPath.endX,
          additionalFlatPath.endZ
        )
      )
    );
  }

  // Bunker floors, walls, lips, and a turf apron all remain level with the
  // green. The easing band prevents a ridge from forming around the lip.
  for (const bunker of bunkers) {
    protection = Math.min(
      protection,
      smoothstep(9, 33, distanceOutsidePolygon(bunker.outer, x, z))
    );
  }
  // Snap floating-point dust at the exact mask boundaries back to a truly
  // level surface; ball/cup placement can then continue to use y=0 exactly.
  if (protection <= 1e-9) return 0;

  const phase = hole * 1.61803398875;
  let relief = 0;
  for (let lobe = 0; lobe < 6; lobe++) {
    const angle = phase + lobe * 2.39996322973 + ((hole * (lobe + 3)) % 7) * 0.071;
    const radius = greenRadius + 34 + ((hole * 19 + lobe * 31) % 48);
    const lobeX = Math.cos(angle) * radius;
    const lobeZ = centerZ + Math.sin(angle) * radius * 0.9;
    const spreadX = 29 + ((hole * 11 + lobe * 7) % 20);
    const spreadZ = 35 + ((hole * 5 + lobe * 13) % 24);
    const normalized =
      ((x - lobeX) * (x - lobeX)) / (spreadX * spreadX) +
      ((z - lobeZ) * (z - lobeZ)) / (spreadZ * spreadZ);
    const amplitude =
      lobe === (hole + 2) % 6
        ? -0.58
        : 0.72 + ((hole * 7 + lobe * 5) % 9) * 0.13;
    relief += amplitude * Math.exp(-0.5 * normalized);
  }

  return Math.max(-0.65, Math.min(2.25, relief)) * protection * edgeFade;
}

function cedarTerrainCellIndex(coordinates: readonly number[], value: number): number {
  if (value < coordinates[0] || value > coordinates[coordinates.length - 1]) return -1;
  let low = 0;
  let high = coordinates.length - 1;
  while (low + 1 < high) {
    const middle = Math.floor((low + high) / 2);
    if (coordinates[middle] <= value) low = middle;
    else high = middle;
  }
  return Math.min(low, coordinates.length - 2);
}

/**
 * Height of the triangles that the player actually sees, rather than the
 * smooth source field sampled to build them. Keeping scenery on this surface
 * avoids tiny gaps where a point sample differs from mesh interpolation.
 */
export function cedarRenderedTerrainHeightAt(
  holeNumber: number,
  targetFt: number,
  bunkers: readonly ResolvedCedarBunker[],
  x: number,
  z: number,
  additionalFlatPath?: CedarFlatPath
): number {
  const xCell = cedarTerrainCellIndex(CEDAR_TERRAIN_X_COORDS, x);
  const zCell = cedarTerrainCellIndex(CEDAR_TERRAIN_Z_COORDS, z);
  if (xCell < 0 || zCell < 0) return 0;

  const x0 = CEDAR_TERRAIN_X_COORDS[xCell];
  const x1 = CEDAR_TERRAIN_X_COORDS[xCell + 1];
  const z0 = CEDAR_TERRAIN_Z_COORDS[zCell];
  const z1 = CEDAR_TERRAIN_Z_COORDS[zCell + 1];
  const across = (x - x0) / (x1 - x0);
  const down = (z - z0) / (z1 - z0);
  const heightAt = (sampleX: number, sampleZ: number) =>
    cedarTerrainHeightAt(
      holeNumber,
      targetFt,
      bunkers,
      sampleX,
      sampleZ,
      additionalFlatPath
    );
  const nearLeft = heightAt(x0, z0);
  const nearRight = heightAt(x1, z0);
  const farLeft = heightAt(x0, z1);
  const farRight = heightAt(x1, z1);

  // This is the same far-left to near-right diagonal used by
  // makeCedarTerrainGeometry in CedarScenery.
  if (across + down <= 1) {
    return nearLeft * (1 - across - down) + nearRight * across + farLeft * down;
  }
  return (
    nearRight * (1 - down) +
    farLeft * (1 - across) +
    farRight * (across + down - 1)
  );
}

export const CEDAR_BUNKER_OPENING_SCALE = 0.7;

/**
 * Signed radial field shared with the ground shader: negative is the opening,
 * positive is intact turf. The opening sits between the 0.65 floor and 0.78
 * wall-top contours, preserving an irregular recessed edge without a mask
 * texture or a triangulated 900 ft plane.
 */
export function cedarBunkerOpeningField(
  bunker: ResolvedCedarBunker,
  x: number,
  z: number
): number {
  const dx = x - bunker.x;
  const dz = z - bunker.centerZ;
  const cosR = Math.cos(bunker.rotation);
  const sinR = Math.sin(bunker.rotation);
  const localX = dx * cosR + dz * sinR;
  const localZ = -dx * sinR + dz * cosR;
  const normalizedX = localX / (bunker.width * 0.5);
  const normalizedZ = localZ / (bunker.length * 0.5);
  const angle = Math.atan2(normalizedZ, normalizedX);
  const boundary =
    CEDAR_BUNKER_OPENING_SCALE *
    (1 +
      0.16 * Math.sin(angle * 3 + bunker.seed * 0.17) +
      0.08 * Math.sin(angle * 5 + bunker.seed * 0.31));
  return Math.hypot(normalizedX, normalizedZ) - boundary;
}

export interface CedarTreeSpec {
  x: number;
  z: number;
  height: number;
  width: number;
  variant: number;
  tone: number;
}

export const CEDAR_TREE_ALPHA_TEST = 0.42;
export const CEDAR_TREE_GROUND_OVERLAP_FT = 0.08;

/** Find the first visible trunk row using the same cutoff as the material. */
export function cedarTreeAtlasBaselineInsets(
  rgba: Uint8Array,
  atlasSize: number,
  tileCount: number,
  alphaTest: number
): number[] {
  const tileWidth = atlasSize / tileCount;
  if (
    !Number.isInteger(tileWidth) ||
    rgba.length < atlasSize * atlasSize * 4 ||
    atlasSize < 2
  ) {
    return Array.from({ length: tileCount }, () => 0);
  }

  const alphaThreshold = Math.ceil(alphaTest * 255);
  return Array.from({ length: tileCount }, (_, variant) => {
    let baselineRow = atlasSize - 1;
    let found = false;
    for (let row = atlasSize - 1; row >= 0 && !found; row--) {
      for (let column = variant * tileWidth; column < (variant + 1) * tileWidth; column++) {
        if (rgba[(row * atlasSize + column) * 4 + 3] >= alphaThreshold) {
          baselineRow = row;
          found = true;
          break;
        }
      }
    }
    return (atlasSize - 1 - baselineRow) / (atlasSize - 1);
  });
}

/** Center a card so its first visible trunk pixel slightly enters the turf. */
export function cedarTreeCardCenterY(
  renderedTerrainY: number,
  height: number,
  baselineInset: number
): number {
  return (
    renderedTerrainY +
    height * (0.5 - baselineInset) -
    CEDAR_TREE_GROUND_OVERLAP_FT
  );
}

function seededRandom(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (Math.imul(state, 1664525) + 1013904223) >>> 0;
    return state / 0x100000000;
  };
}

export function cedarTreeSpecsForHole(holeNumber: number): CedarTreeSpec[] {
  const hole = Math.max(1, Math.min(9, Math.round(holeNumber)));
  const random = seededRandom(0xc3da12 + hole * 7919);
  const trees: CedarTreeSpec[] = [];

  for (const side of [-1, 1]) {
    for (let i = 0; i < 10; i++) {
      const variant = Math.floor(random() * 4);
      const height = 20 + random() * 13;
      trees.push({
        x: side * (50 + random() * 30 + i * 1.25),
        z: -42 - i * 12 - random() * 8,
        height,
        width: height * (variant === 2 ? 0.25 + random() * 0.025 : 0.2 + random() * 0.025),
        variant,
        tone: (random() - 0.5) * 0.08,
      });
    }
  }

  // Two loosely staggered depths close the artificial picket-fence gaps while
  // staying in the same four instanced draw calls (64 cards = 128 triangles).
  for (let i = 0; i < 44; i++) {
    const angle = -1.02 + (i / 43) * 2.04 + (random() - 0.5) * 0.055;
    const radius = 168 + (i % 2) * 24 + random() * 38;
    const variant = Math.floor(random() * 4);
    const height = 17 + random() * 16;
    let x = Math.sin(angle) * radius + (random() - 0.5) * 7;
    if (Math.abs(x) < 18) x = (x < 0 ? -1 : 1) * (18 + random() * 7);
    trees.push({
      x,
      z: -Math.cos(angle) * radius + (random() - 0.5) * 6,
      height,
      width: height * (variant === 2 ? 0.24 + random() * 0.03 : 0.19 + random() * 0.03),
      variant,
      tone: (random() - 0.5) * 0.08,
    });
  }
  return trees;
}
